import { CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useCompleteVenueOnboarding } from "@/features/venues/model/useVenueProfile";
import {
  VENUE_CATEGORY_LABELS,
  type VenueProfile,
} from "@/features/venues/types";

interface Props {
  profile: VenueProfile;
  email: string | undefined;
  onBack: () => void;
}

export function ReviewStep({ profile, email, onBack }: Props) {
  const navigate = useNavigate();
  const complete = useCompleteVenueOnboarding();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await complete.mutateAsync();
    navigate("/venue", { replace: true });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold tracking-display-tight text-copy">
          Ready to go?
        </h2>
        <p className="mt-1 text-sm text-copy-muted">
          Double-check your details. You can edit any of this from your venue profile later.
        </p>
      </div>

      <div className="flex items-center gap-4 rounded-lg border border-border bg-surface p-4">
        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md border border-border bg-surface-sunken">
          {profile.logo_url ? (
            <img
              src={profile.logo_url}
              alt={profile.business_name}
              className="h-full w-full object-cover"
            />
          ) : null}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-copy">
            {profile.business_name || "—"}
          </p>
          <p className="truncate text-xs text-copy-muted">
            {profile.category ? VENUE_CATEGORY_LABELS[profile.category] : "Category not set"}
          </p>
        </div>
      </div>

      <div className="rounded-md border border-border bg-surface">
        <SummaryRow label="Business name" value={profile.business_name || "Not set"} />
        <SummaryRow label="Address" value={profile.address || "Not set"} />
        <SummaryRow
          label="Primary contact"
          value={
            [profile.contact_first_name, profile.contact_last_name]
              .filter(Boolean)
              .join(" ") || "Not set"
          }
        />
        {profile.contact_role && (
          <SummaryRow label="Role" value={profile.contact_role} />
        )}
        <SummaryRow label="Contact phone" value={profile.contact_phone ?? "Not set"} />
        <SummaryRow label="Contact email" value={email ?? "Not set"} />
        <SummaryRow
          label="Category"
          value={
            profile.category ? VENUE_CATEGORY_LABELS[profile.category] : "Not set"
          }
        />
        <SummaryRow
          label="Logo"
          value={profile.logo_url ? "Uploaded" : "Not uploaded"}
          last
        />
      </div>

      {complete.isError && (
        <p className="text-sm text-destructive-foreground">
          {(complete.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
            "Could not finish setup. Review your details and try again."}
        </p>
      )}

      <div className="flex items-center justify-between border-t border-border pt-4">
        <button
          type="button"
          onClick={onBack}
          className="rounded-md border border-border bg-surface px-3 py-2 text-sm hover:bg-surface-sunken"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={complete.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
        >
          <CheckCircle2 className="h-4 w-4" />
          {complete.isPending ? "Finishing…" : "Finish setup"}
        </button>
      </div>
    </form>
  );
}

function SummaryRow({
  label,
  value,
  last = false,
}: {
  label: string;
  value: string;
  last?: boolean;
}) {
  return (
    <div
      className={
        "flex items-center justify-between gap-3 px-4 py-3 text-sm" +
        (last ? "" : " border-b border-border")
      }
    >
      <span className="text-copy-muted">{label}</span>
      <span className="min-w-0 truncate text-right font-medium text-copy">{value}</span>
    </div>
  );
}
